import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { PageTimeStat } from '../interfaces';

export function surveyChartOptions(pageTimeStats: PageTimeStat[]): ChartOptions<'bar' | 'line'> {
    return {
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    label: (tooltipItem: TooltipItem<'bar' | 'line'>) => {
                        const pageCount = pageTimeStats[tooltipItem.dataIndex].PageCount;
                        const mainLabel =
                            tooltipItem.datasetIndex === 0 ? `${tooltipItem.raw} ${$Resources.SurveyPageSeconds}` : `${$Resources.SurveyViews} ${pageCount}`;
                        return mainLabel;
                    },
                    labelColor: (tooltipItem: TooltipItem<'bar' | 'line'>) => {
                        const index = tooltipItem.datasetIndex;
                        if (index === 1) {
                            return {
                                backgroundColor: 'rgba(255, 0, 255, 1)',
                                borderColor: 'rgba(255, 0, 255, 1)',
                            };
                        } else {
                            return {
                                backgroundColor: 'rgba(31,169,176,1)',
                                borderColor: 'rgba(31,169,176,1)',
                            };
                        }
                    },
                    title: (tooltipItems: TooltipItem<'bar' | 'line'>[]) => {
                        const dataIndex = tooltipItems[0].dataIndex;
                        const fullLabel = pageTimeStats?.[dataIndex]
                            ? `${$Resources.SurveyPage} ${pageTimeStats[dataIndex].PageIndex} - ${pageTimeStats[dataIndex].FirstQuestion}`
                            : '';
                        return fullLabel;
                    },
                    footer: (tooltipItems: TooltipItem<'bar' | 'line'>[]) => {
                        const pageCount = pageTimeStats[tooltipItems[0].dataIndex].PageCount;
                        const firstPageCount = pageTimeStats[0]?.PageCount || 1;
                        const percentage = ((pageCount / firstPageCount) * 100).toFixed(0);
                        const percentageLabel = `${percentage}% ${$Resources.SurveyGraphLabel}`;
                        return percentageLabel;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                type: 'linear',
                position: 'left',
                title: {
                    display: true,
                    text: $Resources.SurveyPageTimeDuration,
                },
                ticks: {
                    callback: (value: string | number) => {
                        if (typeof value === 'number') {
                            if (value < 60) {
                                return `${value} s`;
                            } else {
                                const minutes = Math.floor(value / 60);
                                const seconds = value % 60;
                                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds} min`;
                            }
                        }
                        return value;
                    },
                },
            },
            y1: {
                beginAtZero: true,
                type: 'linear',
                position: 'right',
                title: {
                    display: true,
                    text: $Resources.SurveyVisitsOnPageCount,
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
        },
    };
}

export function surveyChartData(pageTimeStats?: PageTimeStat[]): ChartData<'bar' | 'line'> {
    return {
        labels: pageTimeStats?.map((x) => {
            const labelText = `${$Resources.SurveyPage} ${x.PageIndex.toString()} - ${x.FirstQuestion}`;
            return labelText.length > 14 ? `${labelText.substring(0, 14)}...` : labelText;
        }),
        datasets: [
            {
                type: 'bar' as const,
                label: $Resources.SurveyPageTimeDuration,
                data: pageTimeStats?.map((x) => +x.AverageDuration.toFixed(1)) ?? [],
                backgroundColor: 'rgba(31,169,176,1)',
                borderColor: 'rgba(31,169,176,1)',
                borderWidth: 1,
                yAxisID: 'y',
                order: 2,
                maxBarThickness: 90,
            },
            {
                type: 'line' as const,
                label: $Resources.SurveyVisitsOnPageCount,
                data: pageTimeStats?.map((x) => x.PageCount) ?? [],
                backgroundColor: 'rgba(255, 0, 255, 1)',
                borderColor: 'rgba(255, 0, 255, 1)',
                borderWidth: 2,
                fill: false,
                yAxisID: 'y1',
                order: 1,
            },
        ],
    };
}
